import React, { useState, useEffect, useLayoutEffect } from 'react';
import { theme } from '../theme';
import { signIn, signInSecondStep, signUp, requestPasswordReset, resetPassword } from '../utils/user';
import { NATIVE_API_BASE } from '../utils/nativeShell';
import DropPinWordmark from '../components/DropPinWordmark';
import PasswordInput from '../components/PasswordInput';
import { FREE_LIMITS } from '../utils/tier';
import { APP_VERSION } from '../utils/version';
// v0.45.17 — Lucide check + plan icons for the multi-step signup flow.
// v0.245.0 — and the suite sign-in's rail and buttons.
import {
  Check, X as XIcon, Sparkles, Building2, LayoutDashboard, Users, Shield, LogIn,
  ShieldCheck, ArrowLeft, ExternalLink,
} from 'lucide-react';
import './LoginScreen.css';

/**
 * LoginScreen — v0.26.0 (sign-in only) → v0.31.0 (signup + forgot + reset)
 * → v0.245.0 (the suite sign-in).
 *
 * One screen, four modes:
 *   'signin'  — username + password (DropPin Users first, InSpec proxy fallback)
 *   'signup'  — Create account (new DropPin tenant)
 *   'forgot'  — Enter email, get reset link
 *   'reset'   — Token from URL (?token=…), set new password
 *
 * Mode is auto-detected from the URL on mount:
 *   /reset-password?token=…  → 'reset'
 *   anything else            → 'signin'
 *
 * Modes 'signup' / 'forgot' are toggled by the user via inline links.
 * No router involvement — AppShell's auth gate just renders this screen
 * when there's no session, and the screen flips its own mode internally.
 *
 * On any successful auth (signin / signup / reset), calls onSignedIn(user)
 * so AppShell hides the login and renders the rest of the app.
 *
 * v0.245.0 — Brett, 2026-09-28: "Make the logins match inspec." Every mode now
 * sits in InSpec's sign-in: the slate Synerflo rail beside the form card, the
 * suite's form kit (./LoginScreen.css), "Protected by Synerflo ID" under it.
 * Signing in is one shared moment across the suite, and the software is
 * Synerflo's, so the vendor's name is on it — never a tenant's. Two things are
 * DropPin's own: sign-up's plan picker takes the whole card (the rail steps
 * aside for it), and the DropPin wordmark heads the card wherever the rail is
 * not shown — DropPin is used on phones, where the rail is hidden.
 */
export default function LoginScreen({ onSignedIn }) {
  // URL-driven initial mode for the reset link case.
  const initialMode = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (window.location.pathname === '/reset-password' && p.get('token')) return 'reset';
    } catch (_) {}
    return 'signin';
  })();
  const [mode, setMode] = useState(initialMode);
  const [wide, setWide] = useState(false);   // sign-up's plan picker needs the whole card

  const head = HEADINGS[mode] || HEADINGS.signin;

  return (
    <div className="login-container">
      <div className={'auth-shell' + (wide && mode === 'signup' ? ' is-wide' : '')}>
        {/* ── Brand rail (suite slate) — hidden on a phone ── */}
        <aside className="auth-hero" aria-hidden="true">
          <div className="auth-hero-mark">
            <span className="auth-hero-mark-tile"><ShieldCheckMark /></span>
            <span className="auth-hero-mark-text">
              <span className="auth-hero-mark-title">DropPin</span>
              <span className="auth-hero-mark-sub">by Synerflo</span>
            </span>
          </div>

          <div className="hero-eyebrow">Synerflo software suite</div>
          <h1 className="hero-headline">Your Synerflo tools,<br />one secure sign-in.</h1>
          <p className="hero-blurb">
            Sign in once to reach every app your team is licensed for — InSpec,
            InSpecPDF, DropPin and more.
          </p>
          <ul className="hero-points">
            <li>
              <span className="hp-ic"><LayoutDashboard size={15} strokeWidth={2.3} /></span>
              <span>
                <strong>One launcher</strong>
                <span className="hp-sub">Open every product you have a seat for from a single dashboard.</span>
              </span>
            </li>
            <li>
              <span className="hp-ic"><Users size={15} strokeWidth={2.3} /></span>
              <span>
                <strong>Team &amp; licences</strong>
                <span className="hp-sub">Owners and admins manage members and seats in one place.</span>
              </span>
            </li>
            <li>
              <span className="hp-ic"><Shield size={15} strokeWidth={2.3} /></span>
              <span>
                <strong>One secure password</strong>
                <span className="hp-sub">Set it once — it works across the whole suite.</span>
              </span>
            </li>
          </ul>
          <div className="hero-foot">Synerflo</div>
        </aside>

        {/* ── Form card ── */}
        <div className="auth-card">
          <div className="auth-card-brand"><DropPinWordmark /></div>
          <div className="login-card-header">
            <div className="login-card-title">{head.title}</div>
            <div className="login-card-subtitle">{head.subtitle}</div>
          </div>

          {mode === 'signin' && <SignInForm onSignedIn={onSignedIn} setMode={setMode} />}
          {mode === 'signup' && <SignUpForm onSignedIn={onSignedIn} setMode={setMode} onWide={setWide} />}
          {mode === 'forgot' && <ForgotForm setMode={setMode} />}
          {mode === 'reset'  && <ResetForm  onSignedIn={onSignedIn} setMode={setMode} />}

          <div className="login-version">v{APP_VERSION}</div>

          {/* The sign-in is Synerflo ID's, the suite SSO — the attribution every
              product in the suite carries. */}
          <div className="login-sso">
            <img className="login-sso-mark" src="/brand/synerflo-mark.svg" alt="" width={18} height={18} />
            <span className="login-sso-text">
              Protected by <span className="login-sso-word">Synerflo</span> ID
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const HEADINGS = {
  signin: { title: 'Sign in', subtitle: 'Welcome back. Sign in to reach your Synerflo software.' },
  signup: { title: 'Create your DropPin account', subtitle: 'Free or Pro. Change or cancel any time — Free has no time limit.' },
  forgot: { title: 'Reset password', subtitle: 'We’ll email you a reset link.' },
  reset:  { title: 'Set a new password', subtitle: 'Choose a new password, then you’re signed in.' },
};

function ShieldCheckMark() {
  return (
    <span className="auth-hero-mark-ic">
      <Shield size={19} strokeWidth={1.9} />
      <Check size={9} strokeWidth={3} className="auth-hero-mark-tick" />
    </span>
  );
}

// ───────── Sign In ─────────
function SignInForm({ onSignedIn, setMode }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [mfa, setMfa] = useState(null);   // 2026-09-28 — the challenge after a right password

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (!username.trim() || !password) { setErr('Enter your email or username, and your password.'); return; }
    setBusy(true); setErr(null);
    const out = await signIn(username.trim(), password);
    setBusy(false);
    if (out.mfa) { setMfa(out.mfa); return; }
    if (out.error) { setErr(out.error); return; }
    onSignedIn(out.user);
  }

  if (mfa) {
    return <MfaStep challenge={mfa} onSignedIn={onSignedIn}
      onRestart={(why) => { setMfa(null); setPassword(''); setErr(why || null); }} />;
  }

  return (
    <form onSubmit={submit}>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-login-user">Email or username</label>
        <input id="dp-login-user" className="form-input" autoFocus value={username}
          onChange={e => setUsername(e.target.value)} autoComplete="username"
          placeholder="Enter email or username" disabled={busy} />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-login-password">Password</label>
        {/* v0273 — DropPin was the only app in the suite with no way to see what
            you typed; on a phone a mistyped password you cannot check is a
            support call. The suite's PasswordInput carries the eye now. */}
        <PasswordInput id="dp-login-password" className="form-input" value={password}
          onChange={e => setPassword(e.target.value)} autoComplete="current-password"
          placeholder="Enter password" disabled={busy} />
      </div>
      <div className="login-row">
        <button type="button" className="login-link-btn" onClick={() => setMode('forgot')}>Forgot your password?</button>
      </div>
      {err && <div role="alert" className="login-error">{err}</div>}
      <button type="submit" className="btn btn-primary login-submit" disabled={busy}>
        {busy
          ? <span className="login-spin" aria-hidden="true" />
          : <LogIn size={16} aria-hidden="true" />}
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <div className="login-foot">
        New to DropPin? <button type="button" className="login-link-btn" onClick={() => setMode('signup')}>Create an account</button>
      </div>
    </form>
  );
}

// ───────── Sign Up ─────────
//
// v0.45.17 — Multi-step signup. Brett: "the form with free version/
//            paid version specs". Replaces the single-form signup
//            with a three-step flow:
//
//   Step 1 — Plan picker. Two side-by-side cards (Free / Pro) showing
//            the FREE_LIMITS spec vs Pro unlocks. Picking a plan
//            advances to step 2. The chosen plan persists across step
//            transitions in case the user backs out and changes.
//
//   Step 2 — Account details. Name + email + password + company. The
//            selected plan badge sits up top so the user knows what
//            they're signing up for. Back button returns to step 1.
//
//   Step 3 — (Pro only) Billing placeholder. Brett: "not full
//            connected tonight". Stripe is queued; for now Pro signups
//            land on a "Payment coming soon — 14-day Pro trial
//            activated" screen, then submit creates the account with
//            tier='pro'. Free signups skip step 3 entirely.
//
// All three forms route through the same signUp() helper, which now
// accepts a `tier` field. Backend wiring to record the tier on the
// tenant is a separate rev.

function SignUpForm({ onSignedIn, setMode, onWide }) {
  // Step machine: 'plan' → 'details' → ('pay' if pro) → submit.
  const [step, setStep]   = useState('plan');
  const [tier, setTier]   = useState('free');
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [company,   setCompany]   = useState('');
  const [phone,     setPhone]     = useState('');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);

  // v0.245.0 — the two plans side by side need the whole card; the rail
  // steps aside for that step only, and comes back when this form goes.
  // A LAYOUT effect: the card's width changes before the next step is painted,
  // not one frame after it (a plain effect drew the details form wide first).
  useLayoutEffect(() => {
    if (onWide) onWide(step === 'plan');
    return () => { if (onWide) onWide(false); };
  }, [step, onWide]);

  async function doSignUp() {
    if (!firstName.trim() || !email.trim() || !password) {
      setErr('First name, email and password are required.');
      return;
    }
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    setBusy(true); setErr(null);
    const out = await signUp({
      email:     email.trim(),
      password,
      firstName: firstName.trim(),
      lastName:  lastName.trim(),
      company:   company.trim() || undefined,
      phone:     phone.trim() || undefined,
      tier,
    });
    setBusy(false);
    if (out.error) { setErr(out.error); return; }
    onSignedIn(out.user);
  }

  if (step === 'plan') {
    return (
      <PlanPickerStep
        selected={tier}
        onSelect={(t) => { setTier(t); setStep('details'); }}
        onBackToSignin={() => setMode('signin')}
      />
    );
  }

  if (step === 'details') {
    return (
      <DetailsStep
        tier={tier}
        firstName={firstName} setFirstName={setFirstName}
        lastName={lastName}   setLastName={setLastName}
        email={email}         setEmail={setEmail}
        password={password}   setPassword={setPassword}
        company={company}     setCompany={setCompany}
        phone={phone}         setPhone={setPhone}
        err={err}
        busy={busy}
        onBack={() => { setErr(null); setStep('plan'); }}
        onContinue={() => {
          // Validate here so we don't drop into the pay step with bad data.
          if (!firstName.trim() || !email.trim() || !password) {
            setErr('First name, email and password are required.');
            return;
          }
          if (password.length < 8) {
            setErr('Password must be at least 8 characters.');
            return;
          }
          setErr(null);
          // Free skips the pay step; Pro lands on the billing placeholder.
          if (tier === 'pro') setStep('pay');
          else doSignUp();
        }}
        onSignin={() => setMode('signin')}
      />
    );
  }

  // tier === 'pro' && step === 'pay'
  return (
    <PayStep
      tier={tier}
      email={email}
      busy={busy}
      err={err}
      onBack={() => { setErr(null); setStep('details'); }}
      onConfirm={doSignUp}
    />
  );
}

// ── Plan picker ───────────────────────────────────────────────────────

const PLAN_SPECS = {
  // Numbers come from FREE_LIMITS so they stay in sync with the
  // gates elsewhere in the app. "Pro" is currently uncapped — when
  // we add headroom limits these move into a PRO_LIMITS map.
  free: {
    name:      'Free',
    price:     'AU$0',
    cadence:   'forever',
    headline:  'Try the full flow on one project.',
    features:  [
      { ok: true,  text: `${FREE_LIMITS.activeProjects} active project` },
      { ok: true,  text: `${FREE_LIMITS.defectsPerProject} defects per project` },
      { ok: true,  text: `${FREE_LIMITS.drawingsPerReport} drawing per report` },
      { ok: true,  text: `${FREE_LIMITS.reportsPerMonth} report shares per month` },
      { ok: true,  text: `${FREE_LIMITS.photoStorageMB} MB photo storage` },
      { ok: true,  text: 'PDF reports (with DropPin watermark)' },
      { ok: false, text: 'White-label PDFs' },
      { ok: false, text: 'Public share links' },
      { ok: false, text: 'Invite subbies to log in' },
    ],
    cta: 'Start free',
  },
  pro: {
    name:      'Pro',
    price:     'AU$29',
    cadence:   'per user / month',
    headline:  'Everything, no caps. Send unlimited reports.',
    features:  [
      { ok: true, text: 'Unlimited active projects' },
      { ok: true, text: 'Unlimited defects per project' },
      { ok: true, text: 'Unlimited drawings per report' },
      { ok: true, text: 'Unlimited report shares' },
      { ok: true, text: '5 GB photo storage' },
      { ok: true, text: 'White-label PDF (your logo, no watermark)' },
      { ok: true, text: 'Public share links to clients & builders' },
      { ok: true, text: 'Invite subbies — they see only their items' },
      { ok: true, text: 'Priority email support' },
    ],
    cta: 'Start Pro',
    badge: '14-day trial',
  },
};

// A plan's colour over the card, as a wash. v0.245.0 — was `${accent}10`, a hex
// alpha glued onto a CSS variable ("var(--brand-600)10"), which is no colour at
// all: the selected card and the plan badge never had their tint.
const wash = (accent, pct) => `color-mix(in srgb, ${accent} ${pct}%, ${theme.surface})`;

function PlanPickerStep({ selected, onSelect, onBackToSignin }) {
  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 12,
      }}>
        <PlanCard
          plan={PLAN_SPECS.free}
          tierKey="free"
          selected={selected === 'free'}
          onSelect={() => onSelect('free')}
          accent={theme.text}
          icon={<Building2 size={18} />}
        />
        <PlanCard
          plan={PLAN_SPECS.pro}
          tierKey="pro"
          selected={selected === 'pro'}
          onSelect={() => onSelect('pro')}
          accent={theme.brand}
          icon={<Sparkles size={18} />}
        />
      </div>
      <div className="login-foot">
        Already have an account?{' '}
        <button type="button" className="login-link-btn" onClick={onBackToSignin}>Sign in</button>
      </div>
    </div>
  );
}

function PlanCard({ plan, tierKey, selected, onSelect, accent, icon }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-plan={tierKey}
      style={{
        textAlign: 'left',
        background: selected ? wash(accent, 7) : theme.surface,
        border: `2px solid ${selected ? accent : theme.border}`,
        borderRadius: 10,
        padding: 16,
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: theme.text,
        position: 'relative',
        transition: 'border-color 0.12s ease, background 0.12s ease',
      }}
    >
      {plan.badge && (
        <div style={{
          position: 'absolute', top: 10, right: 10,
          background: accent, color: theme.surface,
          fontSize: 10, fontWeight: 700, padding: '2px 7px',
          borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>{plan.badge}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent }}>
        {icon}
        <div style={{ fontSize: 18, fontWeight: 800 }}>{plan.name}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: theme.text }}>{plan.price}</div>
        <div style={{ fontSize: 11, color: theme.textLight }}>{plan.cadence}</div>
      </div>
      <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>{plan.headline}</div>

      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {plan.features.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12 }}>
            {f.ok ? (
              <Check size={14} style={{ color: theme.success, flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
            ) : (
              <XIcon size={14} style={{ color: theme.textLight, flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
            )}
            <span style={{ color: f.ok ? theme.text : theme.textLight }}>{f.text}</span>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 14,
        background: selected ? accent : 'transparent',
        // The card's own ground as the ink: white on a light card, slate on a dark one.
        color: selected ? theme.surface : accent,
        border: `1.5px solid ${accent}`,
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 13, fontWeight: 700,
        textAlign: 'center',
      }}>
        {selected ? `${plan.cta} →` : `Choose ${plan.name}`}
      </div>
    </button>
  );
}

// The chosen plan, above the details and the trial.
function PlanBadge({ tier, onChange, children }) {
  const accent = tier === 'pro' ? theme.brand : theme.text;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      background: wash(accent, 7),
      border: `1px solid ${wash(accent, 25)}`,
      borderRadius: 8, padding: '8px 12px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {tier === 'pro' ? <Sparkles size={16} style={{ color: accent }} /> : <Building2 size={16} style={{ color: accent }} />}
        <div style={{ fontSize: 13, fontWeight: 700, color: accent }}>{children}</div>
      </div>
      {onChange && <button type="button" className="login-link-btn" onClick={onChange}>Change plan</button>}
    </div>
  );
}

// ── Account details ───────────────────────────────────────────────────

function DetailsStep({
  tier,
  firstName, setFirstName,
  lastName,  setLastName,
  email,     setEmail,
  password,  setPassword,
  company,   setCompany,
  phone,     setPhone,
  err, busy,
  onBack, onContinue, onSignin,
}) {
  const planName = PLAN_SPECS[tier]?.name || 'Free';
  return (
    <form onSubmit={(e) => { e.preventDefault(); onContinue(); }}>
      <PlanBadge tier={tier} onChange={onBack}>{planName} plan</PlanBadge>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="dp-su-first">First name</label>
          <input id="dp-su-first" className="form-input" autoFocus value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="dp-su-last">Last name</label>
          <input id="dp-su-last" className="form-input" value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-su-email">Email</label>
        <input id="dp-su-email" className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="dp-su-company">Company (optional)</label>
          <input id="dp-su-company" className="form-input" value={company} onChange={e => setCompany(e.target.value)} autoComplete="organization" placeholder="Your business name" />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="dp-su-phone">Phone (optional)</label>
          <input id="dp-su-phone" className="form-input" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" placeholder="04…" />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-su-password">Password</label>
        <PasswordInput id="dp-su-password" className="form-input" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" minLength={8} />
        <div className="form-hint">At least 8 characters.</div>
      </div>

      {err && <div role="alert" className="login-error">{err}</div>}
      <button type="submit" className="btn btn-primary login-submit" disabled={busy}>
        {busy
          ? (tier === 'pro' ? 'Continuing…' : 'Creating account…')
          : (tier === 'pro' ? 'Continue to payment' : 'Create free account')}
      </button>
      <div className="login-foot">
        Already have an account?{' '}
        <button type="button" className="login-link-btn" onClick={onSignin}>Sign in</button>
      </div>
    </form>
  );
}

// ── Pay step (Pro only) ───────────────────────────────────────────────

function PayStep({ tier, email, busy, err, onBack, onConfirm }) {
  // Stripe wiring is a follow-up rev. Brett: "not full connected tonight".
  // For tonight we accept the signup and start a 14-day Pro trial so the
  // user lands in the Pro experience and we don't lose the signup if
  // they bounce off a paywall.
  return (
    <div>
      <PlanBadge tier={tier}>Pro plan · AU$29 / user / month</PlanBadge>

      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, color: theme.text }}>Start your 14-day Pro trial</div>
      <p className="login-lede">
        We're finalising our payment provider. For now your account ships with
        a 14-day Pro trial — every feature unlocked, no card required. We'll
        email <strong style={{ color: theme.text }}>{email || 'you'}</strong> with
        a billing setup link before the trial ends.
      </p>

      <div style={{
        marginBottom: 16,
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 12,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
          What you get during the trial
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {PLAN_SPECS.pro.features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12 }}>
              <Check size={14} style={{ color: theme.success, flexShrink: 0, marginTop: 1 }} strokeWidth={2.5} />
              <span style={{ color: theme.text }}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {err && <div role="alert" className="login-error">{err}</div>}
      <button type="button" className="btn btn-primary login-submit" onClick={onConfirm} disabled={busy}>
        {busy ? 'Starting trial…' : 'Start Pro trial'}
      </button>
      <button type="button" className="login-back-btn" onClick={onBack}>Back</button>
      <div className="login-foot">
        No card required. We'll send a billing setup link before the trial ends.
      </div>
    </div>
  );
}

// ───────── Forgot password ─────────
function ForgotForm({ setMode }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) { setErr('Enter the email you signed up with.'); return; }
    setBusy(true); setErr(null); setSent(null);
    const out = await requestPasswordReset(email.trim());
    setBusy(false);
    if (out.error) { setErr(out.error); return; }
    setSent(out.message || 'Check your email for the reset link.');
  }

  return (
    <form onSubmit={submit}>
      <p className="login-lede">
        Enter your account email and we'll send you a link to reset your password.
      </p>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-forgot-email">Email</label>
        <input id="dp-forgot-email" className="form-input" autoFocus type="email" value={email}
          onChange={e => setEmail(e.target.value)} autoComplete="email" placeholder="you@yourcompany.com" disabled={busy} />
      </div>
      {err && <div role="alert" className="login-error">{err}</div>}
      {sent && <div role="status" className="login-success">{sent}</div>}
      <button type="submit" className="btn btn-primary login-submit" disabled={busy}>
        {busy ? 'Sending…' : 'Send reset link'}
      </button>
      <button type="button" className="login-back-btn" onClick={() => setMode('signin')}>Back to sign in</button>
    </form>
  );
}

// ───────── Reset password (from token) ─────────
function ResetForm({ onSignedIn, setMode }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [mfa, setMfa] = useState(null);   // 2026-09-28 — two-step sign-in after the reset
  const [token, setToken] = useState('');

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      setToken(p.get('token') || '');
    } catch (_) {}
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!token) { setErr('No reset token. Open the link from your email.'); return; }
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true); setErr(null);
    const out = await resetPassword(token, password);
    // 2026-09-28 — two-step sign-in: the password IS set; the code finishes it.
    if (out.mfa) { setBusy(false); setMfa(out.mfa); return; }
    setBusy(false);
    if (out.error) { setErr(out.error); return; }
    // Clean the URL so the token doesn't sit in history.
    try { window.history.replaceState({}, '', '/'); } catch (_) {}
    onSignedIn(out.user);
  }

  if (mfa) {
    return <MfaStep challenge={mfa} onSignedIn={onSignedIn} passwordSet
      onRestart={(why) => { setMfa(null); setMode('signin'); if (why) setErr(why); }} />;
  }

  return (
    <form onSubmit={submit}>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-reset-password">New password</label>
        <PasswordInput id="dp-reset-password" className="form-input" autoFocus value={password}
          onChange={e => setPassword(e.target.value)} minLength={8} autoComplete="new-password" disabled={busy} />
        <div className="form-hint">At least 8 characters.</div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="dp-reset-confirm">Confirm new password</label>
        <PasswordInput id="dp-reset-confirm" className="form-input" value={confirm}
          onChange={e => setConfirm(e.target.value)} autoComplete="new-password" disabled={busy} />
      </div>
      {err && <div role="alert" className="login-error">{err}</div>}
      <button type="submit" className="btn btn-primary login-submit" disabled={busy}>
        {busy ? 'Saving…' : 'Set password and sign in'}
      </button>
      <button type="button" className="login-back-btn" onClick={() => setMode('signin')}>Back to sign in</button>
    </form>
  );
}

// ───────── Two-step sign-in (2026-09-28, ISO 27001 A.8.5) ─────────
// Passwords live in InSite-ID (Synerflo ID). From its v0.48.0 a right password
// for someone with two-step sign-in on answers "and the code, please"; this is
// that step. Setting it UP happens once in Synerflo ID (the QR code and the
// recovery codes), so a person whose organisation requires it and has not set
// it up is pointed there. v0.245.0 — in the suite's form kit, as every other
// product's code step is (InSpec src/components/auth/MfaCodeStep.jsx).
function hubOrigin() {
  const from = (host) => {
    const parts = String(host || '').split('.');
    return parts.length >= 3 ? 'https://id.' + parts.slice(1).join('.') : '';
  };
  try { const h = from(window.location.hostname); if (h) return h; } catch (_) { /* no window */ }
  try { return from(new URL(NATIVE_API_BASE).hostname); } catch (_) { return ''; }
}

const mfaHeading = { fontSize: 16, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6, color: theme.text };

function MfaStep({ challenge, onSignedIn, onRestart, passwordSet }) {
  const [recovery, setRecovery] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (challenge.mfaEnrolRequired) {
    const hub = hubOrigin();
    return (
      <div data-mfa-step="enrol">
        <h2 style={mfaHeading}><ShieldCheck size={18} aria-hidden="true" /> Two-step sign-in is required</h2>
        <p className="login-lede">
          {passwordSet ? 'Your password is set. ' : ''}{challenge.requiredBy || 'Your organisation'} requires a code from your phone as well as your password.
          Set it up once in Synerflo ID, then sign in here.
        </p>
        {hub
          ? <a className="btn btn-primary login-submit" href={hub + '/#/login'} data-mfa-hub>
              <ExternalLink size={15} aria-hidden="true" /> Set it up in Synerflo ID
            </a>
          : <p className="login-lede">Ask your administrator for the Synerflo ID address.</p>}
        <div style={{ marginTop: 14 }}>
          <button type="button" className="login-link-btn" onClick={() => onRestart()}>
            <ArrowLeft size={13} aria-hidden="true" style={{ verticalAlign: '-2px' }} /> Back to sign in
          </button>
        </div>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    const v = value.trim();
    if (!v || busy) return;
    setBusy(true); setErr(null);
    const out = await signInSecondStep(challenge.mfaToken, recovery ? { recoveryCode: v } : { code: v.replace(/\s+/g, '') });
    setBusy(false);
    if (out.user) { onSignedIn(out.user); return; }
    if (out.expired) { onRestart(out.error); return; }
    setErr(out.error);
  }

  return (
    <form onSubmit={submit} data-mfa-step="code">
      <h2 style={mfaHeading}><ShieldCheck size={18} aria-hidden="true" /> Two-step sign-in</h2>
      <p className="login-lede">
        {passwordSet ? 'Your password is set. ' : ''}{recovery
          ? 'Enter one of the recovery codes you saved when you set this up. Each works once.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </p>
      <div className="form-group">
        <input autoFocus className="form-input" value={value} onChange={(e) => setValue(e.target.value)} data-mfa-input
          aria-label={recovery ? 'Recovery code' : 'Code'} disabled={busy}
          inputMode={recovery ? 'text' : 'numeric'} autoComplete={recovery ? 'off' : 'one-time-code'}
          placeholder={recovery ? 'XXXX-XXXX' : '123456'} maxLength={recovery ? 12 : 7} spellCheck={false} />
      </div>
      {err && <div role="alert" className="login-error" data-mfa-error>{err}</div>}
      <button type="submit" className="btn btn-primary login-submit" disabled={busy || !value.trim()} data-mfa-verify>
        {busy ? 'Checking…' : 'Verify'}
      </button>
      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <button type="button" className="login-link-btn" onClick={() => { setRecovery((r) => !r); setValue(''); setErr(null); }} data-mfa-toggle>
          {recovery ? 'Use my authenticator app' : 'Lost your phone? Use a recovery code'}
        </button>
        <button type="button" className="login-link-btn" onClick={() => onRestart()}>
          <ArrowLeft size={13} aria-hidden="true" style={{ verticalAlign: '-2px' }} /> Start again
        </button>
      </div>
    </form>
  );
}
